import React, { useState } from 'react';
import { useNotify, usePermissions, useRefresh, useTranslate } from 'react-admin';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from '@mui/material';
import { apiClient, type ApiError } from '../../../utils/apiClient';
import { announceKioskNeedsActionChanged } from '../../../hooks/useKioskNeedsActionSummary';
import { hasPermission } from '../../../utils/hasPermission';
import { isSoepayMethod, markRefusalKey, owedByHandAmount } from './refundState';
import type { KioskTransaction } from './types';

export const MarkRefundedByHandButton = ({ record }: { record: KioskTransaction }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const { permissions } = usePermissions();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const owed = owedByHandAmount(record);
  if (!hasPermission(permissions as string[] | null | undefined, 'kiosk-transactions:write:all') || owed === null) return null;

  const submit = async () => {
    setSaving(true);
    try {
      await apiClient.post(`/api/backend/kiosk-transactions/${record.id}/refunded-by-hand`, { note: note.trim() });
      notify(translate('resources.kiosk-transactions.markByHand.done'), { type: 'success' });
      setOpen(false);
      setNote('');
      refresh();
      announceKioskNeedsActionChanged();
    } catch (error) {
      const apiError = error as ApiError;
      const refusal = apiError.errorCode === 'KIOSK_REFUND_NOT_MARKABLE' ? markRefusalKey(apiError.details?.state, record.method) : null;
      const key = refusal === null ? `resources.kiosk-transactions.errors.${apiError.errorCode}` : `resources.kiosk-transactions.${refusal}`;
      refresh();
      announceKioskNeedsActionChanged();
      notify(translate(key, { _: translate('resources.kiosk-transactions.markByHand.failed') }), { type: 'error' });
      if (refusal !== null) setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
        {translate('resources.kiosk-transactions.markByHand.button')}
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{translate('resources.kiosk-transactions.markByHand.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {translate(isSoepayMethod(record.method) ? 'resources.kiosk-transactions.markByHand.bodySoepay' : 'resources.kiosk-transactions.markByHand.body', { amount: owed.toFixed(2), reference: record.receiptReference })}
          </DialogContentText>
          <TextField
            label={translate('resources.kiosk-transactions.markByHand.note')}
            helperText={translate('resources.kiosk-transactions.markByHand.noteHelper')}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            inputProps={{ maxLength: 500, 'data-testid': 'mark-note' }}
            multiline
            minRows={3}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>{translate('ra.action.cancel')}</Button>
          <Button variant="contained" onClick={submit} disabled={saving || note.trim() === ''}>
            {translate('resources.kiosk-transactions.markByHand.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
